import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  LayoutDashboard, Calendar, Package, MessageSquare, Users, LogOut,
  Menu, X, ChevronRight, Search, Filter, Edit, Trash2, Plus,
  Clock, CheckCircle, Camera, Film, Truck, XCircle, IndianRupee,
  TrendingUp, Eye, Star, Key, ClipboardList
} from "lucide-react";
import MyAssignedBookingsTab from "../components/MyAssignedBookingsTab";
import { Toaster, toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dec8cbf5-12ea-4fc0-855f-362ec3363e42/artifacts/i11k2q2i_Nammareels%20logo%20IMG_0308.PNG";

// Status Colors and Icons
const STATUS_CONFIG = {
  pending: { color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", icon: Clock, label: "Pending" },
  confirmed: { color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: CheckCircle, label: "Confirmed" },
  shooting: { color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Camera, label: "Shooting" },
  editing: { color: "bg-orange-500/20 text-orange-400 border-orange-500/30", icon: Film, label: "Editing" },
  delivered: { color: "bg-green-500/20 text-green-400 border-green-500/30", icon: Truck, label: "Delivered" },
  cancelled: { color: "bg-red-500/20 text-red-400 border-red-500/30", icon: XCircle, label: "Cancelled" }
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, isOpen, setIsOpen }) => {
  const { user, logout } = useAuth();

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "bookings", label: "Bookings", icon: Calendar },
  ];

  // Add "My Assigned Bookings" for Admin/Editor (non super_admin)
  if (user?.role === "editor") {
    navItems.push({ id: "my-bookings", label: "My Assigned", icon: ClipboardList });
  }

  navItems.push(
    { id: "packages", label: "Packages", icon: Package },
    { id: "testimonials", label: "Testimonials", icon: MessageSquare }
  );

  if (user?.role === "super_admin") {
    // Super admin also gets My Assigned after Testimonials
    navItems.push({ id: "my-bookings", label: "My Assigned", icon: ClipboardList });
    navItems.push({ id: "users", label: "Users", icon: Users });
  }

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/10 transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <img src={LOGO_URL} alt="Nammareels" className="h-10" />
            <p className="text-white/50 text-xs font-body mt-2 uppercase tracking-wider">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-body text-sm ${
                    activeTab === item.id
                      ? "bg-[#dc2626] text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`}
                  data-testid={`nav-${item.id}`}
                >
                  <Icon size={18} />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-white/10">
            <div className="mb-4">
              <p className="text-white font-body text-sm">{user?.name}</p>
              <p className="text-white/50 text-xs">{user?.email}</p>
              <Badge className="mt-2 bg-[#dc2626]/20 text-[#dc2626] border-[#dc2626]/30 text-xs">
                {user?.role === "super_admin" ? "Super Admin" : "Editor"}
              </Badge>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="w-full border-white/20 text-white/60 hover:bg-white/5"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              Logout
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Tab
const DashboardTab = ({ stats }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  
  const statCards = [
    { label: "Total Bookings", value: stats?.totalBookings || 0, icon: Calendar, color: "text-blue-400" },
    { label: "Pending", value: stats?.pendingBookings || 0, icon: Clock, color: "text-yellow-400" },
    { label: "In Progress", value: (stats?.confirmedBookings || 0) + (stats?.shootingBookings || 0) + (stats?.editingBookings || 0), icon: Camera, color: "text-purple-400" },
    { label: "Delivered", value: stats?.deliveredBookings || 0, icon: Truck, color: "text-green-400" },
    { label: "This Week", value: stats?.recentBookings || 0, icon: TrendingUp, color: "text-cyan-400" },
    // Revenue only visible to Super Admin
    ...(isSuperAdmin ? [{ label: "Revenue", value: `₹${(stats?.totalRevenue || 0).toLocaleString()}`, icon: IndianRupee, color: "text-[#dc2626]" }] : []),
  ];

  return (
    <div className="space-y-6">
      <h2 className="font-heading text-2xl text-white uppercase">Dashboard</h2>

      {/* Stats Grid */}
      <div className={`grid grid-cols-2 md:grid-cols-3 ${isSuperAdmin ? 'lg:grid-cols-6' : 'lg:grid-cols-5'} gap-4`}>
        {statCards.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={i} className="bg-[#0a0a0a] border border-white/10 rounded-xl p-4">
              <Icon className={`${stat.color} mb-2`} size={24} />
              <p className="text-2xl font-heading text-white">{stat.value}</p>
              <p className="text-white/50 text-xs font-body uppercase tracking-wider">{stat.label}</p>
            </div>
          );
        })}
      </div>

      {/* Status Breakdown */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl p-6">
        <h3 className="font-heading text-lg text-white uppercase mb-4">Booking Status Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
            const Icon = config.icon;
            const count = stats?.statusBreakdown?.[status] || 0;
            return (
              <div key={status} className={`border rounded-lg p-3 ${config.color}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={16} />
                  <span className="text-sm font-body">{config.label}</span>
                </div>
                <p className="text-2xl font-heading">{count}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Bookings Tab
const BookingsTab = ({ bookings, users, onUpdate, onRefresh }) => {
  const { user, api } = useAuth();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editData, setEditData] = useState({});
  const [assigningId, setAssigningId] = useState(null);

  const filteredBookings = bookings.filter(b => {
    const matchesSearch = !search || 
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      b.phone.includes(search) ||
      (b.email && b.email.toLowerCase().includes(search.toLowerCase()));
    const matchesStatus = statusFilter === "all" || b.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleUpdate = async () => {
    try {
      await api.patch(`/admin/bookings/${selectedBooking.id}`, editData);
      toast.success("Booking updated successfully");
      setSelectedBooking(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Super Admin: Assign booking to user
  const handleAssign = async (bookingId, assignedToUserId) => {
    try {
      setAssigningId(bookingId);
      await api.patch(`/admin/bookings/${bookingId}/assign`, { assignedToUserId });
      toast.success("Booking assigned successfully");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign");
    } finally {
      setAssigningId(null);
    }
  };

  // Get assignable users (editors and super_admins)
  const assignableUsers = users.filter(u => !u.isBlocked);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h2 className="font-heading text-2xl text-white uppercase">Bookings</h2>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <Input
              placeholder="Search name, phone, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-white/5 border-white/10 text-white pl-9 w-full sm:w-64"
              data-testid="booking-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white w-full sm:w-40" data-testid="status-filter">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-white/10">
              <SelectItem value="all" className="text-white">All Status</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <SelectItem key={key} value={key} className="text-white">{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bookings List */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Customer</th>
                <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Plan</th>
                <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Status</th>
                <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Assigned</th>
                <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Date</th>
                <th className="text-right p-4 text-white/60 font-body text-sm uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.map((booking) => {
                const statusConfig = STATUS_CONFIG[booking.status];
                const StatusIcon = statusConfig?.icon || Clock;
                const isAssigned = booking.assignedToUserId || booking.assignedTo;
                const assignedUserName = booking.assignedToName;
                
                return (
                  <tr key={booking.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`booking-row-${booking.id}`}>
                    <td className="p-4">
                      <p className="text-white font-body">{booking.name}</p>
                      <p className="text-white/50 text-sm">{booking.phone}</p>
                      {booking.email && <p className="text-white/40 text-xs">{booking.email}</p>}
                    </td>
                    <td className="p-4">
                      <p className="text-white font-body text-sm">{booking.planName}</p>
                      {user?.role === "super_admin" && (
                        <p className="text-[#dc2626] font-heading">₹{booking.price?.toLocaleString()}</p>
                      )}
                    </td>
                    <td className="p-4">
                      <Badge className={`${statusConfig?.color} border`}>
                        <StatusIcon size={12} className="mr-1" />
                        {statusConfig?.label}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {/* Assignment Column - Different display based on role */}
                      {user?.role === "super_admin" ? (
                        <div className="space-y-1">
                          {!isAssigned ? (
                            <Select 
                              value="" 
                              onValueChange={(userId) => handleAssign(booking.id, userId)}
                              disabled={assigningId === booking.id}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 text-white w-32 h-8 text-xs">
                                <SelectValue placeholder={assigningId === booking.id ? "Assigning..." : "Assign..."} />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0a0a0a] border-white/10">
                                {assignableUsers.map((u) => (
                                  <SelectItem key={u.id} value={u.id} className="text-white text-xs">
                                    {u.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <div>
                              <p className="text-white/80 text-sm font-body">{assignedUserName}</p>
                              <p className={`text-xs mt-1 ${booking.handledByAssignee ? 'text-green-400' : 'text-yellow-400'}`}>
                                Handled: {booking.handledByAssignee ? 'Yes ✓' : 'No'}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-white/60 text-sm font-body">
                          {assignedUserName || "-"}
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-white/50 text-sm font-body">
                      {new Date(booking.eventDate).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-white/60 hover:text-white hover:bg-white/10"
                          onClick={() => { setSelectedBooking(booking); setEditData({ status: booking.status, assignedTo: booking.assignedTo, notes: booking.notes }); }}
                          data-testid={`edit-booking-${booking.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        {user?.role === "super_admin" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => handleDelete(booking.id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredBookings.length === 0 && (
            <div className="p-8 text-center text-white/50 font-body">No bookings found</div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Edit Booking</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white font-body">{selectedBooking.name}</p>
                <p className="text-white/60 text-sm">{selectedBooking.phone}</p>
                <p className="text-[#dc2626] font-heading mt-2">{selectedBooking.planName} - ₹{selectedBooking.price?.toLocaleString()}</p>
                {selectedBooking.eventDate && <p className="text-white/50 text-sm mt-1">Event: {selectedBooking.eventDate}</p>}
                {selectedBooking.message && <p className="text-white/40 text-sm mt-2 italic">"{selectedBooking.message}"</p>}
              </div>

              <div>
                <Label className="text-white/80">Status</Label>
                <Select value={editData.status} onValueChange={(v) => setEditData({...editData, status: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                      <SelectItem key={key} value={key} className="text-white">{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white/80">Assign To</Label>
                <Select value={editData.assignedTo || "unassigned"} onValueChange={(v) => setEditData({...editData, assignedTo: v === "unassigned" ? null : v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    <SelectItem value="unassigned" className="text-white">Unassigned</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id} className="text-white">{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white/80">Internal Notes</Label>
                <Input
                  value={editData.notes || ""}
                  onChange={(e) => setEditData({...editData, notes: e.target.value})}
                  placeholder="Add notes..."
                  className="bg-white/5 border-white/10 text-white mt-1"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" className="border-white/20 text-white/60" onClick={() => setSelectedBooking(null)}>
                  Cancel
                </Button>
                <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={handleUpdate} data-testid="save-booking-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Packages Tab
const PackagesTab = ({ packages, onRefresh }) => {
  const { user, api } = useAuth();
  const [isEditing, setIsEditing] = useState(null);
  const [formData, setFormData] = useState({});

  const openEdit = (pkg = null) => {
    if (pkg) {
      setFormData({ ...pkg, features: pkg.features.join("\n") });
    } else {
      setFormData({ planName: "", price: 0, features: "", isPopular: false, isActive: true });
    }
    setIsEditing(pkg?.id || "new");
  };

  const handleSave = async () => {
    try {
      const data = {
        ...formData,
        price: Number(formData.price),
        features: formData.features.split("\n").filter(f => f.trim())
      };

      if (isEditing === "new") {
        await api.post("/admin/packages", data);
        toast.success("Package created");
      } else {
        await api.patch(`/admin/packages/${isEditing}`, data);
        toast.success("Package updated");
      }
      setIsEditing(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/admin/packages/${id}`);
      toast.success("Package deleted");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-white uppercase">Packages</h2>
        {user?.role === "super_admin" && (
          <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={() => openEdit()} data-testid="add-package-btn">
            <Plus size={16} /> Add Package
          </Button>
        )}
      </div>

      {/* Packages Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {packages.map((pkg) => (
          <div key={pkg.id} className={`bg-[#0a0a0a] border rounded-xl p-5 ${pkg.isPopular ? 'border-[#dc2626]' : 'border-white/10'} ${!pkg.isActive ? 'opacity-50' : ''}`}>
            {pkg.isPopular && <Badge className="bg-[#dc2626] text-white mb-2">Popular</Badge>}
            {!pkg.isActive && <Badge className="bg-white/10 text-white/50 mb-2">Inactive</Badge>}
            <h3 className="font-heading text-lg text-white uppercase">{pkg.planName}</h3>
            {/* Price only visible to Super Admin */}
            {user?.role === "super_admin" && pkg.price !== undefined && (
              <p className="text-[#dc2626] font-heading text-3xl my-2">₹{pkg.price.toLocaleString()}</p>
            )}
            {user?.role !== "super_admin" && (
              <div className="h-10 my-2" /> /* Spacer to maintain layout */
            )}
            <ul className="space-y-1 mb-4">
              {(pkg.features || []).slice(0, 4).map((f, i) => (
                <li key={i} className="text-white/60 text-sm font-body flex items-start gap-2">
                  <CheckCircle size={14} className="text-[#dc2626] mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
              {(pkg.features || []).length > 4 && <li className="text-white/40 text-xs">+{pkg.features.length - 4} more</li>}
            </ul>
            {user?.role === "super_admin" && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1 border-white/20 text-white/60" onClick={() => openEdit(pkg)}>
                  <Edit size={14} />
                </Button>
                <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => handleDelete(pkg.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      <Dialog open={!!isEditing} onOpenChange={() => setIsEditing(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              {isEditing === "new" ? "Add Package" : "Edit Package"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Plan Name</Label>
              <Input value={formData.planName || ""} onChange={(e) => setFormData({...formData, planName: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Price (₹)</Label>
              <Input type="number" value={formData.price || ""} onChange={(e) => setFormData({...formData, price: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Features (one per line)</Label>
              <textarea value={formData.features || ""} onChange={(e) => setFormData({...formData, features: e.target.value})} rows={5} className="w-full bg-white/5 border border-white/10 text-white mt-1 rounded-md p-2 text-sm" />
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-white/80 text-sm">
                <input type="checkbox" checked={formData.isPopular || false} onChange={(e) => setFormData({...formData, isPopular: e.target.checked})} className="rounded" />
                Mark as Popular
              </label>
              <label className="flex items-center gap-2 text-white/80 text-sm">
                <input type="checkbox" checked={formData.isActive !== false} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} className="rounded" />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" className="border-white/20 text-white/60" onClick={() => setIsEditing(null)}>Cancel</Button>
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Testimonials Tab
const TestimonialsTab = ({ testimonials, onRefresh }) => {
  const { api } = useAuth();
  const [isEditing, setIsEditing] = useState(null);
  const [formData, setFormData] = useState({});

  const openEdit = (t = null) => {
    if (t) {
      setFormData({ ...t });
    } else {
      setFormData({ name: "", roleOrType: "", message: "", rating: 5, isActive: true });
    }
    setIsEditing(t?.id || "new");
  };

  const handleSave = async () => {
    try {
      const data = { ...formData, rating: Number(formData.rating) };
      if (isEditing === "new") {
        await api.post("/admin/testimonials", data);
        toast.success("Testimonial created");
      } else {
        await api.patch(`/admin/testimonials/${isEditing}`, data);
        toast.success("Testimonial updated");
      }
      setIsEditing(null);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Testimonial deleted");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-white uppercase">Testimonials</h2>
        <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={() => openEdit()} data-testid="add-testimonial-btn">
          <Plus size={16} /> Add Testimonial
        </Button>
      </div>

      {/* Testimonials Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {testimonials.map((t) => (
          <div key={t.id} className={`bg-[#0a0a0a] border border-white/10 rounded-xl p-5 ${!t.isActive ? 'opacity-50' : ''}`}>
            <div className="flex gap-1 mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={14} className={i < t.rating ? "fill-[#dc2626] text-[#dc2626]" : "text-white/20"} />
              ))}
            </div>
            <p className="text-white/80 font-body italic mb-3">"{t.message}"</p>
            <p className="text-white font-heading">{t.name}</p>
            <p className="text-white/50 text-sm font-body">{t.roleOrType}</p>
            {!t.isActive && <Badge className="bg-white/10 text-white/50 mt-2">Inactive</Badge>}
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="flex-1 border-white/20 text-white/60" onClick={() => openEdit(t)}>
                <Edit size={14} />
              </Button>
              <Button size="sm" variant="outline" className="border-red-500/30 text-red-400" onClick={() => handleDelete(t.id)}>
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit Modal */}
      <Dialog open={!!isEditing} onOpenChange={() => setIsEditing(null)}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">
              {isEditing === "new" ? "Add Testimonial" : "Edit Testimonial"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Name</Label>
              <Input value={formData.name || ""} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Role / Type</Label>
              <Input value={formData.roleOrType || ""} onChange={(e) => setFormData({...formData, roleOrType: e.target.value})} placeholder="e.g. Content Creator" className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Message</Label>
              <textarea value={formData.message || ""} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={3} className="w-full bg-white/5 border border-white/10 text-white mt-1 rounded-md p-2 text-sm" />
            </div>
            <div>
              <Label className="text-white/80">Rating</Label>
              <Select value={String(formData.rating || 5)} onValueChange={(v) => setFormData({...formData, rating: Number(v)})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  {[5,4,3,2,1].map(n => <SelectItem key={n} value={String(n)} className="text-white">{n} Stars</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-white/80 text-sm">
              <input type="checkbox" checked={formData.isActive !== false} onChange={(e) => setFormData({...formData, isActive: e.target.checked})} className="rounded" />
              Active (visible on website)
            </label>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" className="border-white/20 text-white/60" onClick={() => setIsEditing(null)}>Cancel</Button>
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={handleSave}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Users Tab (Super Admin Only)
const UsersTab = ({ users, onRefresh }) => {
  const { api } = useAuth();
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", password: "", role: "editor" });
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleCreate = async () => {
    try {
      await api.post("/auth/register-admin", formData);
      toast.success("Admin user created");
      setIsCreating(false);
      setFormData({ name: "", email: "", phone: "", password: "", role: "editor" });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };

  const toggleBlock = async (userId, isBlocked) => {
    try {
      await api.patch(`/admin/users/${userId}/block`, { isBlocked: !isBlocked });
      toast.success(isBlocked ? "User unblocked" : "User blocked");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role: newRole });
      toast.success("Role updated");
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleResetPassword = async () => {
    setPasswordError("");
    
    // Client-side validation
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      setPasswordError("Password must contain at least 1 uppercase letter");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      setPasswordError("Password must contain at least 1 number");
      return;
    }

    try {
      const response = await api.patch(`/admin/users/${resetPasswordUser.id}/password`, {
        newPassword: newPassword
      });
      
      if (response.data.success) {
        toast.success(`Password reset for ${resetPasswordUser.name}`);
        setResetPasswordUser(null);
        setNewPassword("");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to reset password";
      setPasswordError(errorMsg);
      toast.error(errorMsg);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl text-white uppercase">Users</h2>
        <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={() => setIsCreating(true)} data-testid="add-user-btn">
          <Plus size={16} /> Add Admin
        </Button>
      </div>

      {/* Users Table */}
      <div className="bg-[#0a0a0a] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-white/60 font-body text-sm uppercase">User</th>
              <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Role</th>
              <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Status</th>
              <th className="text-left p-4 text-white/60 font-body text-sm uppercase">Joined</th>
              <th className="text-right p-4 text-white/60 font-body text-sm uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-white/5">
                <td className="p-4">
                  <p className="text-white font-body">{u.name}</p>
                  <p className="text-white/50 text-sm">{u.email}</p>
                </td>
                <td className="p-4">
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                    <SelectTrigger className="bg-white/5 border-white/10 text-white w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      <SelectItem value="super_admin" className="text-white">Super Admin</SelectItem>
                      <SelectItem value="editor" className="text-white">Editor</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <Badge className={u.isBlocked ? "bg-red-500/20 text-red-400" : "bg-green-500/20 text-green-400"}>
                    {u.isBlocked ? "Blocked" : "Active"}
                  </Badge>
                </td>
                <td className="p-4 text-white/50 text-sm font-body">
                  {new Date(u.createdAt).toLocaleDateString()}
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="border-white/20 text-white/60 hover:text-white hover:bg-white/10" 
                      onClick={() => { setResetPasswordUser(u); setNewPassword(""); setPasswordError(""); }}
                      data-testid={`reset-password-${u.id}`}
                    >
                      <Key size={14} />
                    </Button>
                    <Button size="sm" variant="outline" className={u.isBlocked ? "border-green-500/30 text-green-400" : "border-red-500/30 text-red-400"} onClick={() => toggleBlock(u.id, u.isBlocked)}>
                      {u.isBlocked ? "Unblock" : "Block"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Add Admin User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Password</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className="bg-white/5 border-white/10 text-white mt-1" />
            </div>
            <div>
              <Label className="text-white/80">Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10">
                  <SelectItem value="editor" className="text-white">Editor</SelectItem>
                  <SelectItem value="super_admin" className="text-white">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" className="border-white/20 text-white/60" onClick={() => setIsCreating(false)}>Cancel</Button>
              <Button className="bg-[#dc2626] hover:bg-[#b91c1c]" onClick={handleCreate}>Create User</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Modal */}
      <Dialog open={!!resetPasswordUser} onOpenChange={() => { setResetPasswordUser(null); setNewPassword(""); setPasswordError(""); }}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase">Reset Password</DialogTitle>
          </DialogHeader>
          {resetPasswordUser && (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-white font-body">{resetPasswordUser.name}</p>
                <p className="text-white/60 text-sm">{resetPasswordUser.email}</p>
              </div>
              
              {passwordError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm font-body">
                  {passwordError}
                </div>
              )}
              
              <div>
                <Label className="text-white/80">New Password</Label>
                <Input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  placeholder="Min 8 chars, 1 uppercase, 1 number"
                  className="bg-white/5 border-white/10 text-white mt-1" 
                  data-testid="reset-password-input"
                />
                <p className="text-white/40 text-xs mt-2">
                  Password must be at least 8 characters with 1 uppercase letter and 1 number.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" className="border-white/20 text-white/60" onClick={() => { setResetPasswordUser(null); setNewPassword(""); setPasswordError(""); }}>
                  Cancel
                </Button>
                <Button 
                  className="bg-[#dc2626] hover:bg-[#b91c1c]" 
                  onClick={handleResetPassword}
                  disabled={!newPassword}
                  data-testid="confirm-reset-password-btn"
                >
                  Reset Password
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Admin Dashboard Component
export default function AdminDashboard() {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignedBookings, setAssignedBookings] = useState([]);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, packagesRes, testimonialsRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/packages"),
        api.get("/admin/testimonials")
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setPackages(packagesRes.data);
      setTestimonials(testimonialsRes.data);

      // Fetch users if super admin
      try {
        const usersRes = await api.get("/admin/users");
        setUsers(usersRes.data);
      } catch (e) {
        // Not super admin, ignore
      }

      // Fetch assigned bookings for the current user
      try {
        const assignedRes = await api.get("/admin/my-assigned-bookings");
        setAssignedBookings(assignedRes.data);
      } catch (e) {
        // Might fail for some reason, ignore
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
  fetchData();

  const interval = setInterval(() => {
    fetchData();
  }, 10000);

  return () => clearInterval(interval);
}, []);

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <Toaster position="top-right" richColors />
      
      {/* Sidebar */}
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        isOpen={sidebarOpen} 
        setIsOpen={setSidebarOpen} 
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-[#0a0a0a] border-b border-white/10 px-4 md:px-6 py-4 flex items-center justify-between">
          <button 
            className="lg:hidden text-white p-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-white/50 font-body text-sm">Nammareels.in</span>
            <ChevronRight size={16} className="text-white/30" />
            <span className="text-white font-body text-sm capitalize">{activeTab}</span>
          </div>
          <a href="/" target="_blank" className="text-white/50 hover:text-white text-sm font-body flex items-center gap-1">
            <Eye size={14} /> View Site
          </a>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {activeTab === "dashboard" && <DashboardTab stats={stats} />}
          {activeTab === "bookings" && <BookingsTab bookings={bookings} users={users} onRefresh={fetchData} />}
          {activeTab === "my-bookings" && <MyAssignedBookingsTab assignedBookings={assignedBookings} onRefresh={fetchData} />}
          {activeTab === "packages" && <PackagesTab packages={packages} onRefresh={fetchData} />}
          {activeTab === "testimonials" && <TestimonialsTab testimonials={testimonials} onRefresh={fetchData} />}
          {activeTab === "users" && <UsersTab users={users} onRefresh={fetchData} />}
        </main>
      </div>
    </div>
  );
}
